
    import Vue from "vue";
    import {Component, Prop, Watch} from "vue-property-decorator";
    import {
        TutorialAttemptApi
    } from "@/Api/TutorialAttemptApi";
    import {VueInject} from "@/VueIntegration/VueInjectable";
    import {Slideshow} from "@/slideshow";
    import ResultModal from "../Interactivity/ResultModal.vue";
    import SequenceItem, {MenuItem} from "../Interactivity/SequenceItem.vue";
    import VueButton from "../UI/VueButton.vue";
    import {draggable, dropTarget} from "@/VueIntegration/Draggable";
    import Modal from "../UI/Modal.vue";
    import {announce} from "@/UI/Announce";
    import {IAskCategoryQuestion, ICompletedCategoryQuestion} from "@/Core/Interactions";
    import {IAppearance, ITutorialVariables} from "@/Core/TutorialVariables";

    @Component({
        components: {ResultModal, SequenceItem, VueButton, Modal},
        directives: {
            "draggable": draggable,
            "drop-target": dropTarget
        }
    })
    export default class Categorise extends Vue
    {
        question: IAskCategoryQuestion|null = null;
        options: Option[] = [];
        completedQuestion: ICompletedCategoryQuestion|null = null;
        showResultModal = false;
        showAccessibilityModal = false;
        appearance: IAppearance;
        isSubmittingAnswers = false;
        errorMessage: string|null = null;
        lockId: number|null = null;

        categories: Category[] = [];

        //Props
        @Prop({ type: String, required: true }) readonly id: string;
        @Prop({ type: String, required: false }) readonly code: string;

        //Services
        @VueInject("Slideshow") readonly slideshow: Slideshow;
        @VueInject("TutorialAttemptApi") readonly api: TutorialAttemptApi;
        @VueInject("ITutorialVariables") readonly variables: ITutorialVariables;

        // computed

        get inputType() {
            return this.question!.maxAnswers > 1 ? 'checkbox' : 'radio';
        }

        get isAnswered() {
            return this.completedQuestion !== null;
        }

        get questionCode() {
            return this.code || this.id;
        }

        get categoryAnswers() {
            let categoryAnswers = [];
            for (let category of this.categories) {
                for (let o of category.chosenOptions){
                    categoryAnswers.push({
                        answerItem: o.code,
                        category: category.categoryCode
                    })
                }
            }

            return categoryAnswers;
        }

        get menuItems(): MenuItem[] {
            return [
                {
                    text: "Remove",
                    eventCode: "remove",
                    eventData: null
                }
            ].concat(this.customMenuItems);
        }

        get customMenuItems(): MenuItem[] {
            let result = [];

            for (let c of this.categories) {
                result.push(
                    {
                        text: "Move to " + c.categoryName,
                        eventCode: "move",
                        eventData: c.categoryCode
                    }
                );
            }

            return result;
        }

        get canAddMoreItems() {
            return this.question!.maxAnswers > this.categoryAnswers.length
                && !this.isAnswered
                && !this.isSubmittingAnswers;
        }

        mounted() {
            this.enter();
        }

        @Watch("id")
        enter() {
            this.options = [];
            this.showResultModal = false;
            this.isSubmittingAnswers = false;
            this.errorMessage = null;
            this.lockId = null;
            this.appearance = this.slideshow.appearance;
            this.question = <IAskCategoryQuestion> this.api.getQuestion(this.questionCode);
            for (let code in this.question.items) {
                let item = this.question.items[code];
                this.options.push({
                    itemText: item.itemText,
                    code: item.code,
                    isCorrect: null
                });
            }

            this.categories = [];
            for (let categoryCode in this.question.categories) {
                this.categories.push({
                    categoryCode: categoryCode,
                    categoryName: this.question.categories[categoryCode],
                    chosenOptions: []
                });
            }

            this.completedQuestion = <ICompletedCategoryQuestion> this.api.getCompletedQuestion(this.questionCode);
            if (this.completedQuestion) {
                for (let given of this.completedQuestion.givenAnswers) {
                    let option = this.options.find(x => x.code == given.questionAnswerCode);
                    if (option == undefined)
                        continue;
                    this.moveToCode(option, given.category, false);
                    option.isCorrect = given.isCorrect;
                }

                announce("Slide featuring a category question that has been completed.");
            }
            else {
                this.lockId = this.slideshow.lockNext();
                announce("Slide featuring a category question to answer. Please go to the main region of the page.");
            }

            this.slideshow.slideEntered();
        }

        isOptionPlaced(option: Option) {
            for (let c of this.categories) {
                for (let o of c.chosenOptions) {
                    if (o.code === option.code)
                        return true;
                }
            }

            return false;
        }

        remove(option: Option) {
            for (let category of this.categories) {
                let i = category.chosenOptions.findIndex(o => o.code === option.code);
                if (i === -1)
                    continue;

                category.chosenOptions.splice(i, 1);
            }
        }

        moveToCode(option: Option, categoryCode: string, doAnnounce = true) {
            let c = this.categories.find(c => c.categoryCode === categoryCode);
            if (c)
                this.moveTo(option, c, doAnnounce);
        }

        moveTo(option: Option, category: Category, doAnnounce = true) {
            this.remove(option);
            category.chosenOptions.push(option);
            if (doAnnounce)
                announce("Moved " + option.itemText + " to the category " + category.categoryName);
        }

        submitAnswers() {
            if (this.isSubmittingAnswers || this.completedQuestion)
                return;

            let categoryAnswers = this.categoryAnswers;

            if (categoryAnswers.length > this.question!.maxAnswers) {
                this.errorMessage = "Too many answers selected.";
                return;
            }

            if (categoryAnswers.length < this.question!.minAnswers) {
                this.errorMessage = "Too few answers selected.";
                return;
            }

            this.isSubmittingAnswers = true;
            this.api.submitCategoriseQuestion(this.questionCode, categoryAnswers)
                .then(result => {
                    this.isSubmittingAnswers = false;
                    this.completedQuestion = result;
                    console.log(result);
                    this.showResultModal = true;
                    for (let c of this.categories) {
                        for (let o of c.chosenOptions) {
                            o.isCorrect = this.completedQuestion.givenAnswers
                                .find(a => a.questionAnswerCode === o.code)!.isCorrect;
                        }
                    }
                    if (this.lockId != null)
                        this.slideshow.unlockNext(this.lockId);
                })
                .catch(e => {
                    console.error(e);
                    this.errorMessage = "Sorry, there's been an error processing your request. " +
                        "Please try refreshing this page and attempting to answer the question again. " +
                        "If the error persists, please contact " +
                        this.variables.customer.supportEmail;
                });
        }

        finished() {
            this.slideshow.nextSlide();
        }
    }

    interface Option {
        itemText: string;
        code: string;
        isCorrect: boolean|null;
    }

    interface Category {
        categoryCode: string;
        categoryName: string;
        chosenOptions: Option[];
    }
